/**
 * Scroll trigger configuration helpers
 * Handles calculating scroll trigger positions and pivot points
 */

interface ScrollTriggerConfig {
  start: number | 'full' | { element: number; viewport: number } | string
  distance?: number
  scrollDistance?: number
  pivot?: 'center' | 'top' | 'bottom'
}

/**
 * Calculate pivot viewport position
 */
export function calculatePivotViewport(
  elementRect: DOMRect,
  pivot: 'center' | 'top' | 'bottom'
): number {
  if (pivot === 'center') {
    return elementRect.top + elementRect.height / 2
  } else if (pivot === 'top') {
    return elementRect.top
  } else {
    return elementRect.bottom
  }
}

/**
 * Recalculate scroll trigger positions for viewport mode on resize
 */
export function recalculateViewportPositions(
  scrollTrigger: ScrollTriggerConfig,
  currentViewportHeight: number,
  useViewportMode: boolean
): { startViewportPosition: number; endViewportPosition: number } {
  if (useViewportMode) {
    const startConfig = scrollTrigger.start as { element: number; viewport: number }
    const viewportPercent = startConfig.viewport
    const distancePercent = scrollTrigger.distance ?? 100

    const startViewportPosition = (viewportPercent / 100) * currentViewportHeight
    const endViewportPosition = startViewportPosition - (distancePercent / 100) * currentViewportHeight

    return { startViewportPosition, endViewportPosition }
  } else {
    // Recalculate for pivot mode on resize
    const currentViewportCenter = currentViewportHeight / 2
    const currentScrollDistance = (scrollTrigger.scrollDistance ?? 1.0) * currentViewportHeight
    const currentHalfDistance = currentScrollDistance / 2
    const startViewportPosition = currentViewportCenter + currentHalfDistance
    const endViewportPosition = currentViewportCenter - currentHalfDistance

    return { startViewportPosition, endViewportPosition }
  }
}

/**
 * Calculate animation progress
 */
export function calculateAnimationProgress(
  pivotViewport: number,
  startViewportPosition: number,
  endViewportPosition: number,
  useFullViewportMode: boolean,
  elementRect?: DOMRect,
  viewportHeight?: number
): number {
  if (useFullViewportMode && elementRect && viewportHeight !== undefined) {
    // Full: progress 0 = first pixel visible (top of element at bottom of viewport), progress 1 = last pixel gone (bottom of element at top of viewport)
    const top = elementRect.top
    const bottom = elementRect.bottom
    const height = elementRect.height
    if (top >= viewportHeight) {
      return 0
    } else if (bottom <= 0) {
      return 1
    } else {
      const range = viewportHeight + height
      return (viewportHeight - top) / range
    }
  } else if (pivotViewport >= startViewportPosition) {
    return 0
  } else if (pivotViewport <= endViewportPosition) {
    return 1
  } else {
    const range = startViewportPosition - endViewportPosition
    const position = startViewportPosition - pivotViewport
    return position / range
  }
}
